(() => {
  if (customElements.get('nut-back-in-stock')) return;

  const TOAST_CONTAINER_ID = 'NutBackInStockToastContainer';

  function getToastContainer() {
    let container = document.getElementById(TOAST_CONTAINER_ID);
    if (container) return container;

    container = document.createElement('div');
    container.id = TOAST_CONTAINER_ID;
    container.className = 'nut-bis-toast-container';
    container.setAttribute('aria-live', 'polite');
    container.setAttribute('aria-atomic', 'true');
    container.dir = document.documentElement.dir || (document.documentElement.lang?.toLowerCase().startsWith('ar') ? 'rtl' : 'ltr');
    document.body.appendChild(container);
    return container;
  }

  function showToast(text, type = 'success') {
    const container = getToastContainer();
    const toast = document.createElement('div');
    toast.className = `nut-bis-toast is-${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');

    const icon = document.createElement('span');
    icon.className = 'nut-bis-toast__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = type === 'error' ? '!' : '✓';

    const message = document.createElement('span');
    message.className = 'nut-bis-toast__message';
    message.textContent = text;

    toast.append(icon, message);
    container.appendChild(toast);

    requestAnimationFrame(() => toast.classList.add('is-visible'));

    window.setTimeout(() => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 220);
    }, 4500);
  }

  class NutBackInStock extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.nut-back-in-stock__form');
      this.email = this.querySelector('input[name="email"]');
      this.button = this.querySelector('.nut-back-in-stock__submit');
      this.spinner = this.querySelector('.nut-back-in-stock__spinner');
      this.variantIdInput = this.querySelector('[data-nut-bis-variant-id]');
      this.variantTitleInput = this.querySelector('[data-nut-bis-variant-title]');
      this.productUrlInput = this.querySelector('[data-nut-bis-product-url]');
      this.endpoint = this.dataset.endpoint || 'https://n8n.nutbotanicals.com/webhook/nut-back-in-stock-subscribe';
      this.variants = this.readVariants();
      this.buyBlock = this.closest('.nut-pdp-info')?.querySelector('.hdt-product__buy-button') || null;
      this.section = this.closest('.nut-pdp-section') || document;
      this.stickyAtc = this.section.querySelector('hdt-sticky-btn-atc');

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      document.addEventListener('variant:change', this.onVariantChange);
      this.form?.addEventListener('submit', this.onSubmit);

      const initialId = Number(this.dataset.initialVariantId || 0);
      const initialVariant = this.variants.find((v) => Number(v.id) === initialId) || this.variants[0];
      this.applyVariant(initialVariant);
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this.onVariantChange);
      this.form?.removeEventListener('submit', this.onSubmit);
    }

    readVariants() {
      try {
        return JSON.parse(this.querySelector('[data-nut-bis-variants]')?.textContent || '[]');
      } catch (_) {
        return [];
      }
    }

    needsNotification(variant) {
      if (!variant) return false;
      if (variant.available === false) return true;
      return variant.inventory_management === 'shopify' && Number(variant.inventory_quantity) <= 0;
    }

    onVariantChange(event) {
      const variant = event?.detail?.variant;
      if (!variant || !this.variants.some((v) => Number(v.id) === Number(variant.id))) return;
      this.applyVariant(variant);
    }

    applyVariant(variant) {
      if (!variant) return;
      const notify = this.needsNotification(variant);
      this.hidden = !notify;

      if (this.buyBlock) this.buyBlock.hidden = notify;
      if (this.stickyAtc) this.stickyAtc.hidden = notify;

      if (this.variantIdInput) this.variantIdInput.value = variant.id ? `gid://shopify/ProductVariant/${variant.id}` : '';
      if (this.variantTitleInput) this.variantTitleInput.value = variant.title || '';
      if (this.productUrlInput) {
        const base = this.dataset.productUrl || window.location.href.split('?')[0];
        this.productUrlInput.value = `${base}?variant=${variant.id}`;
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.form || !this.email) return;

      if (!this.email.checkValidity()) {
        this.email.reportValidity();
        return;
      }

      const formData = new FormData(this.form);
      if (formData.get('website')) return;

      const payload = {
        email: String(formData.get('email') || '').trim().toLowerCase(),
        shop: this.dataset.shop || '',
        product_id: String(formData.get('product_id') || ''),
        product_handle: String(formData.get('product_handle') || ''),
        product_title: String(formData.get('product_title') || ''),
        variant_id: String(formData.get('variant_id') || ''),
        variant_title: String(formData.get('variant_title') || ''),
        product_url: String(formData.get('product_url') || ''),
        locale: this.dataset.locale || document.documentElement.lang || 'en',
        source: 'nut-pdp-back-in-stock'
      };

      this.setLoading(true);

      try {
        const response = await fetch(this.endpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        let data = {};
        try { data = await response.json(); } catch (_) {}

        if (!response.ok || data.success === false || data.status === 'invalid_request') {
          throw new Error(this.dataset.errorMessage || 'Something went wrong. Please try again.');
        }

        if (data.status === 'already_subscribed') {
          showToast(
            this.dataset.duplicateMessage || 'You’re already on the list for this product.',
            'info'
          );
        } else if (data.status === 'subscribed') {
          showToast(
            this.dataset.successMessage || 'You’re on the list! We’ll notify you when this product is back in stock.',
            'success'
          );
        } else {
          throw new Error(this.dataset.errorMessage || 'Something went wrong. Please try again.');
        }

        this.email.value = '';
      } catch (error) {
        console.error('[NUT Back In Stock]', error);
        showToast(
          this.dataset.errorMessage || 'Something went wrong. Please try again.',
          'error'
        );
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(loading) {
      if (!this.button) return;
      this.button.disabled = loading;
      if (this.spinner) this.spinner.hidden = !loading;
      this.button.setAttribute('aria-busy', loading ? 'true' : 'false');
    }
  }

  customElements.define('nut-back-in-stock', NutBackInStock);
})();
